<template>
  <b-modal
    :id="`schedule-articles-modal`"
    size="lg"
    hide-header-close
    no-close-on-esc
    no-close-on-backdrop
    centered
  >
    <template v-slot:modal-header>
      <h5 class="mb-0">
        Schedule Article Delivery
      </h5>
    </template>
    <section>
      <div
        class="mb-3"
      >
        <p
          class="mb-4"
        >
          Choose the dates and times to publish or pause selected article(s).
        </p>
        <p class="mb-1">
          <b>Schedule Type</b>
        </p>
        <div class="mb-0">
          <input
            v-model="scheduleType"
            type="radio"
            name="scheduleType"
            value="deliver"
            id="deliver"
            class="mr-1"
            @change="clearSchedule()"
          >
          <label for="deliver">Deliver content between selected dates & times.</label>
        </div>
        <div class="mb-0">
          <input
            v-model="scheduleType"
            type="radio"
            name="scheduleType"
            value="pause"
            id="pause"
            class="mr-1"
            @change="clearSchedule()"
          >
          <label for="pause">Pause content between selected dates & times.</label>
        </div>
        <p class="mt-1 mb-1">
          <b>Dates & Times</b>
        </p>
        <p class="mb-0">
          You must, at minimum, select a stat or end date. Dates that are not manually selected will default to to the
          campaign's start/end date.
        </p>
      </div>
      <b-row
        class="gutters-0 new-gutters-xs"
      >
        <b-col
          cols="auto"
        >
          <b-form-group>
            <template v-slot:label>
              <p
                class="mb-0 font-weight-bold"
              >
                Start
              </p>
            </template>
            <div class="time-wrapper mb-2">
              <vue-timepicker
                :class="{ 'pause' : scheduleType === 'pause' }"
                v-model="startTime"
                :minute-interval="5"
                hide-clear-button
                input-width="100%"
              />
            </div>
            <datepicker
              :class="{ 'pause' : scheduleType === 'pause' }"
              :value="startDateValue"
              @selected="setCustomStartDate($event)"
              placeholder="Select Date"
              format="dd/MM/yyyy"
              :open-date="startDateValue ? new Date(startDateValue) : new Date(minStartDate)"
              :highlighted="highlightedDates"
              :disabled-dates="disabledDates"
              :bootstrap-styling="true"
              :monday-first="true"
              :inline="true"
              data-test="schedule-articles-start-date"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="auto"
        >
          <b-form-group>
            <template v-slot:label>
              <p
                class="mb-0 font-weight-bold"
              >
                End
              </p>
            </template>
            <div class="time-wrapper mb-2">
              <vue-timepicker
                :class="{ 'pause' : scheduleType === 'pause' }"
                v-model="endTime"
                :minute-interval="5"
                hide-clear-button
                input-width="100%"
              />
            </div>
            <datepicker
              :class="{ 'pause' : scheduleType === 'pause' }"
              :value="endDateValue"
              @selected="setCustomEndDate($event)"
              placeholder="Select Date"
              format="dd/MM/yyyy"
              :open-date="endDateValue ? new Date(endDateValue) : new Date(maxEndDate)"
              :highlighted="highlightedDates"
              :disabled-dates="disabledDates"
              :bootstrap-styling="true"
              :monday-first="true"
              :inline="true"
              data-test="schedule-articles-end-date"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <small>All times are in UTC.</small>
    </section>
    <template v-slot:modal-footer>
      <b-button
        variant="secondary"
        @click="clearSchedule()"
        :disabled="!canScheduleArticles({ ids: selectedIds })"
        data-test="schedule-articles-btn-clear-schedule"
      >
        Clear schedule
      </b-button>
      <div class="ml-auto">
        <b-button
          variant="tertiary"
          class="mr-2"
          @click="closeModal()"
          data-test="schedule-articles-btn-cancel"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :class="{ 'btn-loading': isScheduleButtonLoading }"
          @click="scheduleArticles()"
          :disabled="isScheduleButtonDisabled()"
          data-test="schedule-articles-btn-confirm"
        >
          Schedule
        </b-button>
      </div>
    </template>
  </b-modal>
</template>
<script>

import { mapState, mapGetters, mapActions } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as contentTypes from '@/modules/content/store/types';

import Datepicker from 'vuejs-datepicker';
import VueTimepicker from 'vue2-timepicker';
import dateHelper from '@/core/mixins/dateHelper';

import ArticlesActionsMixins from '@/modules/content/components/ContentPage/mixins/ArticlesActionsMixins';
import { endOfDay, startOfDay } from 'date-fns';
import 'vue2-timepicker/dist/VueTimepicker.css';

function initialData() {
  return {
    startDateSelected: null,
    startTimeSelected: '00:00',
    endDateSelected: null,
    endTimeSelected: '23:55',
    scheduleTypSelected: 'deliver',
    isScheduleButtonLoading: false,
    isScheduleCleared: false,
    today: new Date(),
  };
}

export default {
  name: 'ScheduleArticlesModal',
  mixins: [
    dateHelper,
    ArticlesActionsMixins,
  ],
  components: {
    Datepicker,
    VueTimepicker,
  },
  props: {
    customProps: {
      type: Object,
      required: true,
      default: () => {
      },
    },
  },
  data() {
    return initialData();
  },
  computed: {
    ...mapState('campaigns', [
      'selectedAdvertiser',
    ]),
    ...mapState('content', [
      'feedId',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_CAMPAIGN_MIN_START_DATE,
      campaignTypes.GET_CAMPAIGN_MAX_END_DATE,
    ]),
    ...mapGetters('content', [
      contentTypes.GET_ARTICLE_BY_ID,
    ]),
    areAllArticlesSelected() {
      if (!this.customProps.hasOwnProperty('areAllArticlesSelected')) return false;
      return this.customProps.areAllArticlesSelected;
    },
    scheduleType: {
      get() {
        const articleScheduleAction = this.getArticleSchedulePropertyValue('action');
        if (!this.isScheduleCleared && articleScheduleAction) {
          return articleScheduleAction === 'PUBLISH' ? 'deliver' : 'pause';
        }
        return this.scheduleTypSelected;
      },
      set(newValue) {
        this.scheduleTypSelected = newValue;
      },
    },
    startTime: {
      get() {
        const articleScheduleStartDateTime = this.getArticleSchedulePropertyValue('from');
        return articleScheduleStartDateTime ? articleScheduleStartDateTime.split('T')[1] : this.startTimeSelected;
      },
      set(newValue) {
        this.startTimeSelected = newValue;
      },
    },
    endTime: {
      get() {
        const articleScheduleEndDateTime = this.getArticleSchedulePropertyValue('to');
        return articleScheduleEndDateTime ? articleScheduleEndDateTime.split('T')[1] : this.endTimeSelected;
      },
      set(newValue) {
        this.endTimeSelected = newValue;
      },
    },
    selectedIds() {
      if (!this.customProps.hasOwnProperty('ids')) return [];
      return this.customProps.ids;
    },
    isBulk() {
      if (!this.customProps.hasOwnProperty('isBulk')) return false;
      return this.customProps.isBulk;
    },
    scheduleStart() {
      const articleScheduleStartDateTime = this.getArticleSchedulePropertyValue('from');
      return articleScheduleStartDateTime ? articleScheduleStartDateTime.split('T')[0] : null;
    },
    scheduleEnd() {
      const articleScheduleEndDateTime = this.getArticleSchedulePropertyValue('to');
      return articleScheduleEndDateTime ? articleScheduleEndDateTime.split('T')[0] : null;
    },
    minStartDate() {
      if (!this.isScheduleCleared && this.scheduleStart) return this.scheduleStart;
      return new Date(this.GET_CAMPAIGN_MIN_START_DATE) > this.today ? this.GET_CAMPAIGN_MIN_START_DATE : this.convertDateToString(this.today);
    },
    maxEndDate() {
      return (!this.isScheduleCleared && this.scheduleEnd) ? this.scheduleEnd : this.GET_CAMPAIGN_MAX_END_DATE;
    },
    startDateValue() {
      if (this.startDateSelected) return this.convertDateToString(this.startDateSelected);
      return !this.isScheduleCleared ? this.scheduleStart : null;
    },
    endDateValue() {
      if (this.endDateSelected) return this.convertDateToString(this.endDateSelected);
      return !this.isScheduleCleared ? this.scheduleEnd : null;
    },
    highlightedDates() {
      return {
        from: startOfDay(Date.parse(this.startDateValue)),
        to: endOfDay(Date.parse(this.endDateValue)),
      };
    },
    disabledDates() {
      return {
        to: this.minStartDate ? new Date(this.minStartDate) : null,
        from: this.maxEndDate ? new Date(this.maxEndDate) : this.today,
        customPredictor: () => !this.canScheduleArticles({
          ids: this.selectedIds,
        }),
      };
    },
  },
  methods: {
    ...mapActions('content', [
      contentTypes.SET_CONTENT_ITEM_SCHEDULE,
    ]),
    isScheduleButtonDisabled() {
      if (!this.startDateSelected || !this.endDateSelected) return true;
      return !this.canScheduleArticles({ ids: this.selectedIds });
    },
    getArticleSchedulePropertyValue(propertyName) {
      if (!this.isBulk || this.selectedIds.length === 1) {
        const article = this.GET_ARTICLE_BY_ID(this.selectedIds[0]);
        if (article?.schedule?.hasOwnProperty(propertyName)) return article.schedule[propertyName];
      }
      return null;
    },
    setCustomStartDate(date) {
      this.startDateSelected = date;
      if (this.endDateValue && date > new Date(this.endDateValue)) this.endDateSelected = date;
      if (!this.endDateSelected) this.endDateSelected = this.maxEndDate;
    },
    setCustomEndDate(date) {
      this.endDateSelected = date;
      if (this.startDateValue && date < new Date(this.startDateValue)) this.startDateSelected = date;
      if (!this.startDateSelected) this.startDateSelected = this.minStartDate;
    },
    async scheduleArticles() {
      this.isScheduleButtonLoading = true;
      const payload = {
        feedId: this.feedId,
        advertiserId: this.selectedAdvertiser.id,
        contentItemIds: this.selectedIds,
        schedule: {
          from: `${this.startDateValue}T${this.startTimeSelected}:00`,
          to: `${this.endDateValue}T${this.endTimeSelected}:00`,
          action: this.scheduleType === 'deliver' ? 'PUBLISH' : 'UNPUBLISH',
        },
      };
      if (this.areAllArticlesSelected) delete payload.contentItemIds;
      if (this.endDateValue === this.GET_CAMPAIGN_MAX_END_DATE) delete payload.schedule.stop;
      try {
        await this.SET_CONTENT_ITEM_SCHEDULE(payload);
        this.$emit('confirmAction');
      } finally {
        this.closeModal();
      }
    },
    clearSchedule() {
      this.startDateSelected = null;
      this.endDateSelected = null;
      this.isScheduleCleared = true;
    },
    resetData() {
      Object.assign(this.$data, initialData());
    },
    closeModal({ fetchArticles = false } = {}) {
      this.resetData();
      this.$root.$emit('bv::hide::modal', 'schedule-articles-modal');
      if (fetchArticles) this.$emit('fetchArticles');
    },
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .modal-dialog {
    max-width: 650px;
  }
  input.display-time[readonly] {
    cursor: default !important;
  }
  .vue__time-picker .dropdown.drop-down ul {
    li:hover {
      background: $link-light;
    }
    li.active, li.active:hover {
      background: $link;
    }
  }
  .pause.vue__time-picker .dropdown.drop-down ul {
    li:hover {
      background: $warning-light;
    }
    li.active, li.active:hover {
      background: $warning;
    }
  }
}
</style>
